import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ChevronDown, MapPin, Search } from "lucide-react";
import SmartDatePicker from "../../../components/SmartDatePicker";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FramerMotion = require("framer-motion");
const motion = FramerMotion.motion;
const AnimatePresence = FramerMotion.AnimatePresence;

const SEARCH_PLACEHOLDERS = [
  "Search location...",
  "Offices in Kenya…",
  "Coworking spaces",
  "Search by host names",
  "Search by country…",
  "Kenya",
  "Ghana",
];

// Used until the public subcategory registry responds
const FALLBACK_SUBCATEGORIES = [
  { id: "6", name: "Co-working Space" },
  { id: "5", name: "Dedicated Desk" },
  { id: "4", name: "Meeting Room" },
  { id: "1", name: "Private Office" },
];

type WorkSearchBarProps = { subcategories?: ListingSubcategory[] };

export default function WorkSearchBar({ subcategories = [] }: WorkSearchBarProps) {
  const history = useHistory();
  const [locationQuery, setLocationQuery] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");

  // Rotate the WHERE placeholder while the input is empty
  useEffect(() => {
    if (locationQuery) return;
    let intervalId: any;
    const timeoutId = setTimeout(() => {
      setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      intervalId = setInterval(() => {
        setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      }, 3500);
    }, 3500);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [locationQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    let dateParams = "";
    if (dateRange) {
      if (dateRange.startDate && dateRange.endDate) {
        dateParams = `&startDate=${encodeURIComponent(dateRange.startDate)}&endDate=${encodeURIComponent(dateRange.endDate)}`;
      } else if (dateRange.specificDate && dateRange.specificDate.length > 0) {
        dateParams = `&specificDate=${encodeURIComponent(dateRange.specificDate.join(","))}`;
      }
    }
    const queryTerm = encodeURIComponent(locationQuery.trim());
    const subParam = selectedSubcategoryId ? `&subCategoryId=${selectedSubcategoryId}` : "";
    history.push(
      `/workspaces-search?query=${queryTerm}&addressContains=${queryTerm}&location=${queryTerm}&type=work${subParam}${dateParams}`
    );
  };

  const options = subcategories.length > 0 ? subcategories : FALLBACK_SUBCATEGORIES;

  return (
    <form
      onSubmit={handleSearch}
      className="grid grid-cols-1 overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_8px_40px_0_rgba(0,0,0,0.08)] sm:grid-cols-2 lg:grid-cols-4"
    >
      <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] lg:border-b-0 lg:border-r-[1px]">
        <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
          Search
        </label>
        <div className="relative flex items-center gap-2 pt-1">
          <MapPin className="size-3.5 shrink-0 text-[#017bef]" />
          <div className="relative h-5 flex-1 overflow-hidden">
            <input
              type="text"
              value={locationQuery}
              onChange={e => setLocationQuery(e.target.value)}
              autoComplete="off"
              aria-label="Search location"
              className="relative z-10 size-full border-0 bg-transparent p-0 text-[14px] text-[#0a0a0a] outline-none focus:outline-none focus:ring-0"
            />
            {!locationQuery && (
              <div className="pointer-events-none absolute inset-0 flex items-center text-[14px] text-[#717182]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={placeholderIndex}
                    initial={{ y: -16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 16, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="block w-full truncate whitespace-nowrap text-left"
                  >
                    {SEARCH_PLACEHOLDERS[placeholderIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] lg:border-b-0 lg:border-r-[1px]">
        <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
          What do you need?
        </label>
        <div className="relative flex items-center pt-1">
          <select
            value={selectedSubcategoryId}
            onChange={e => setSelectedSubcategoryId(e.target.value)}
            aria-label="Workspace type"
            className="h-5 w-full cursor-pointer appearance-none border-0 bg-transparent pr-6 text-[14px] leading-5 text-[#0a0a0a] focus:outline-none"
          >
            <option value="">Coworking</option>
            {options.map(sub => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-0 size-3.5 text-[#717182]" />
        </div>
      </div>

      <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] sm:border-b-0 lg:border-r-[1px]">
        <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
          When?
        </label>
        <div className="pt-1">
          <SmartDatePicker
            value={dateRange}
            onChange={(val: any) => setDateRange(val)}
            placeholder="Select date and time..."
            className="h-5 w-full border-0 bg-transparent p-0 text-[14px] shadow-none [&>svg]:mr-2 [&>svg]:size-3.5 [&>svg]:text-[#017bef]"
          />
        </div>
      </div>

      <div className="flex items-center px-[16px] py-[12px]">
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#017bef] px-6 py-[12px] text-[14px] font-semibold leading-5 text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)] transition-colors hover:bg-[#0062c0]"
        >
          <Search className="size-[15px]" />
          Search Workspaces
        </button>
      </div>
    </form>
  );
}
